import {
  APIComponentType,
  APICurrentUser,
  Entity,
  Lockable,
  PersonalSetting,
} from '@ardoq/api-types';
import { isEqual } from 'lodash';
import { dispatchAction } from '@ardoq/rxbeach';
import { currentUser as CurrentUser } from 'models/currentUser';
import {
  GetPersonalSetting,
  LastUsedReferenceTypeTriple,
  ReferenceTypeName,
  SetPersonalSetting,
} from 'aqTypes';
import { getCurrentUserPermissionContext } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import {
  internalUseOnlyGetCurrentUser,
  isLoadedCurrentUser,
} from 'streams/currentUser/currentUser$';
import {
  type UpdatePersonalSettingUnion,
  setPersonalSettingInBackground,
} from 'streams/currentUser/actions';
import { PermissionContext } from '@ardoq/access-control';

/**
 * UNSAFE: This returns a direct reference to the object used by Backbone, which
 * means that it can be mutated. For anything by looking up values, use
 * `getCurrentUserAttributesClone` instead.
 */
const getCurrentUserAttributes = (): APICurrentUser => {
  return CurrentUser.attributes;
};

const getCurrentUserAttributesClone = (): APICurrentUser => {
  return structuredClone(getCurrentUserAttributes());
};

const getPermissionContext = (): PermissionContext => {
  return getCurrentUserPermissionContext();
};

const currentUserIsOrgAdmin = () => {
  return CurrentUser.isOrgAdmin();
};

const getCurrentOrgId = () => {
  const user = getCurrentUserAttributes();
  return user.organization?._id ?? '';
};

const getCurrentOrgLabel = (): string => {
  const user = getCurrentUserAttributes();
  return user.organization?.label ?? 'Undefined Organization';
};

const getCurrentUserId = () => CurrentUser.getId();

const setCurrentUserAttributes = (
  currentUserAttributes: APICurrentUser,
  options: { silent?: boolean } = { silent: false }
) => {
  CurrentUser.set(currentUserAttributes);
  if (!options.silent) {
    CurrentUser.trigger('sync');
  }
};

const getPersonalSetting: GetPersonalSetting = key =>
  CurrentUser.getPersonalSetting(key);

const setPersonalSetting: SetPersonalSetting = (key, value) => {
  if (isEqual(getPersonalSetting(key), value)) return;
  if (!isLoadedCurrentUser(internalUseOnlyGetCurrentUser())) {
    CurrentUser.setLocalPersonalSetting(key, value);
    return;
  }
  dispatchAction(
    setPersonalSettingInBackground({
      personalSetting: key,
      newValue: value,
    } as UpdatePersonalSettingUnion)
  );
};

const setLastUsedReferenceTypeTriple = (
  sourceComponentType: APIComponentType,
  targetComponentType: APIComponentType,
  referenceTypeName: string
) => {
  const previousSetting: LastUsedReferenceTypeTriple =
    getPersonalSetting(PersonalSetting.LAST_USED_REFERENCE_TYPE_NAME_TRIPLES) ??
    {};
  setPersonalSetting(PersonalSetting.LAST_USED_REFERENCE_TYPE_NAME_TRIPLES, {
    ...previousSetting,
    [sourceComponentType.name]: {
      ...(previousSetting[sourceComponentType.name] ?? {}),
      [targetComponentType.name]: referenceTypeName,
    },
  });
};

const getLastUsedReferenceTypeTriple = (
  sourceComponentType: APIComponentType,
  targetComponentType: APIComponentType
): ReferenceTypeName | null => {
  const setting: LastUsedReferenceTypeTriple =
    getPersonalSetting(PersonalSetting.LAST_USED_REFERENCE_TYPE_NAME_TRIPLES) ??
    {};
  return setting[sourceComponentType.name]?.[targetComponentType.name] ?? null;
};

const canUnlock = (entity: Entity & Lockable): boolean => {
  const user = getCurrentUserAttributes();
  if (!user) return false;
  return user._id === entity.lock || currentUserIsOrgAdmin();
};

/**
 * `currentUserInterface` is a Backbone-era singleton: the readers snapshot the
 * `currentUser` Backbone model at call time, and the writers mutate it
 * imperatively.
 *
 * The app is stream-based now. Join `currentUser$`
 * (`streams/currentUser/currentUser$.ts`) -- or `currentUserPermissionContext$`
 * (`streams/currentUserPermissions/currentUserPermissionContext$.ts`) for
 * permission checks -- at a permitted boundary and thread the resolved user
 * downstream as plain parameters. Pure logic belongs in `userOperations`
 * (`@ardoq/core`) or `permissionsOperations` (`@ardoq/access-control`). The
 * writers should be replaced by dispatching the owning action.
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe and the interface-to-stream substitution table.
 */
export const currentUserInterface = {
  /**
   * @deprecated Join `currentUser$`; its emissions are already plain data, so no clone is needed.
   */
  getCurrentUserAttributesClone,
  /**
   * @deprecated Join `currentUser$` (`streams/currentUser/currentUser$.ts`).
   */
  getCurrentUserAttributes,
  /**
   * @deprecated Join `currentUserPermissionContext$` (`streams/currentUserPermissions/currentUserPermissionContext$.ts`); `permissionsOperations.toPermissionContext` (`@ardoq/access-control`) is the pure builder.
   */
  getPermissionContext,
  /**
   * @deprecated Join `currentUserPermissionContext$` and call `permissionsOperations.isOrgAdmin` (`@ardoq/access-control`), or `userOperations.hasAdminRole(user)` (`@ardoq/core`).
   */
  currentUserIsOrgAdmin,
  /**
   * @deprecated Call `userOperations.getOrgId(user)` (`@ardoq/core`) with the user from `currentUser$`, or join `currentOrganizationId$`.
   */
  getCurrentOrgId,
  /**
   * @deprecated Call `userOperations.getOrganization(user)` (`@ardoq/core`) and read the label, or join `currentOrganization$`.
   */
  getCurrentOrgLabel,
  /**
   * @deprecated Dispatch the action that owns the personal settings update.
   */
  setPersonalSetting,
  /**
   * @deprecated Dispatch the action that owns the current-user update.
   */
  setCurrentUserAttributes,
  /**
   * @deprecated Call `userOperations.getPersonalSetting(user, key)` (`@ardoq/core`) with the user from `currentUser$`.
   */
  getPersonalSetting,
  /**
   * @deprecated Join `currentUser$` and read `_id`, or call `entityOperations.toId` (`@ardoq/core`).
   */
  getCurrentUserId,
  /**
   * @deprecated Dispatch the action that owns the personal settings update.
   */
  setLastUsedReferenceTypeTriple,
  /**
   * @deprecated Call `userOperations.getPersonalSetting(user, key)` (`@ardoq/core`) with the user from `currentUser$`.
   */
  getLastUsedReferenceTypeTriple,
  /**
   * @deprecated Join `currentUserPermissionContext$` and call the matching `permissionsOperations` predicate (`@ardoq/access-control`).
   */
  canUnlock,
};
